import React, { useEffect, useState } from "react";
import { backendUrl, privacy, terms, websiteUrl } from "../../constants";
import {
  getIsPlanValidAction,
  getQuotationDetailAction,
} from "../../actions/stripe";
import { getProfileAction } from "../../actions/user";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { RoutesPaths } from "../../Router/constant";
import PublicPageHeader from "../../components/PublicPageHeader";
import PublicPageFooter from "../../components/PublicPageFooter";
import TimeFormat from "../../components/timeFormat/timeFormat";
import Currency from "../../components/currency";
import AttachmentList from "../payment/AttachmentList";
import QuotationPreviewSkeleton from "./QuotationPreviewSkeleton";
import OtherQuotationAdvisor from "../../components/OtherQuotationAdvisor";
import CurrencyFormat from "../../components/currencyFormat/currencyFormat";

export default function QuotationPreview() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { quotationDetail, quotationDetailLoader } = useSelector(
    (state) => state.stripe,
  );
  const pathnames = location?.pathname?.split("/").filter((x) => x);
  const planId = pathnames?.[pathnames.length - 1];
  const isAdvisorUser = quotationDetail?.company?.isDefault;

  useEffect(() => {
    const getPlanValidDetail = async () => {
      await dispatch(getQuotationDetailAction(planId));
      const resp = await dispatch(getIsPlanValidAction(planId));
      dispatch(getProfileAction());
      if (resp?.data === "Expired" || !resp?.status) {
        navigate(RoutesPaths.PAY_BANKTRANSFER_PUBLIC.replace(":slug", planId));
        // navigate(RoutesPaths.ERROR_PAGE, { replace: true });
      }
    };

    getPlanValidDetail();
  }, [dispatch, planId]);

  useEffect(() => {
    const root = document.documentElement;
    if (isAdvisorUser) {
      root.style.setProperty("--primary-color", "#494DA2");
    } else {
      root.style.setProperty("--primary-color", "#2978b5");
    }
  }, [isAdvisorUser]);

  useEffect(() => {
    const companyId = quotationDetail?.company?._id;
    localStorage.setItem("companyId", companyId);
  }, [quotationDetail]);

  const imagePath = isAdvisorUser
    ? quotationDetail?.createdBy?.profilePic?.filename
    : quotationDetail?.company?.logo;

  const imageUrl = imagePath ? `${backendUrl}${imagePath}` : null;
  const placeholder = "/assets/img/Frame1.svg";
  const company = quotationDetail?.company;
  const creator = quotationDetail?.createdBy;
  const organizerName = company?.isAdvisor
    ? `${creator?.first_name || ""} ${creator?.last_name || ""}`.trim()
    : company?.companyName || "Organizer";
  const organizerRole = isAdvisorUser
    ? "advisor"
    : company?.companyRoleUser?.join(", ") || "";
  const organizerRating = quotationDetail?.rating || company?.rating || 5;
  const organizerLocation =
    company?.country || creator?.country || creator?.location;
  const organizerAbout = company?.about || creator?.about || creator?.bio;
  const organizerJoinedDate = company?.createdAt || creator?.createdAt;
  const chatCount =
    quotationDetail?.chatCreated || quotationDetail?.chatCount || 0;
  const earning = quotationDetail?.earning || quotationDetail?.netEarning || 0;
  const quotationCount =
    quotationDetail?.quotationCreated ||
    quotationDetail?.quotationCount ||
    quotationDetail?.payouts?.length ||
    0;

  const chatCreatedCount = quotationDetail?.chatCreated || quotationDetail?.chatCount || 0;
  const earningCount = Number(quotationDetail?.earning || quotationDetail?.netEarning) || 0;
  const quotationCreatedCount =
    Number(
      quotationDetail?.quotationCreated ||
      quotationDetail?.quotationCount ||
      quotationDetail?.payouts?.length ||
      0) || 0;
  const companiesCreatedCount = quotationDetail?.companiesParticipated || 0;
  const getMetricLabel = (value, singularLabel, pluralLabel) =>
    Number(value) === 1 ? singularLabel : pluralLabel;
  const advisorNumbers = [
    {
      key: "chatCreated",
      label: getMetricLabel(chatCreatedCount, "chat", "chats"),
      icon: "/assets/img/chatNumbers.svg",
      value: chatCreatedCount,
      displayValue: chatCreatedCount,
    },
    {
      key: "earning",
      label: getMetricLabel(earningCount, "earning", "earnings"),
      icon: "/assets/img/earningNumbers.svg",
      value: earningCount,
      displayValue: <CurrencyFormat value={earningCount} decimal={2} />,
    },
    {
      key: "quotationCreated",
      label: getMetricLabel(quotationCreatedCount, "deal", "deals"),
      icon: "/assets/img/quotationNumbers.svg",
      value: quotationCreatedCount,
      displayValue: quotationCreatedCount,
    },
    {
      key: "companiesCreated",
      label: getMetricLabel(companiesCreatedCount, "company", "companies"),
      icon: "/assets/img/companiesNumber.svg",
      value: companiesCreatedCount,
      displayValue: companiesCreatedCount,
      advisorOnly: true,
    },
  ];
  const formatJoinedDate = (date) => {
    if (!date) return "";
    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return "";

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(parsedDate);
  };

  function getTimeAgo(createdAt) {
    const now = Date.now();
    const then = new Date(createdAt).getTime();
    const diff = now - then; // milliseconds

    const units = [
      { name: "year", ms: 1000 * 60 * 60 * 24 * 365 },
      { name: "month", ms: 1000 * 60 * 60 * 24 * 30 },
      { name: "week", ms: 1000 * 60 * 60 * 24 * 7 },
      { name: "day", ms: 1000 * 60 * 60 * 24 },
      { name: "hour", ms: 1000 * 60 * 60 },
      { name: "minute", ms: 1000 * 60 },
      { name: "second", ms: 1000 },
    ];

    for (let { name, ms } of units) {
      const val = Math.floor(diff / ms);
      if (val > 0) {
        return `${val} ${name}${val > 1 ? "s" : ""} ago`;
      }
    }
    return "just now";
  }

  return (
    <div className="quotationPreviewWrapper">
      <PublicPageHeader />
      {quotationDetailLoader ? (
        <QuotationPreviewSkeleton />
      ) : (
        <>
          <div className="qpHeroBannerImage">
            <div
              className="qpHeroBanner qpHeroBannerImageShade"
              style={{
                background: isAdvisorUser
                  ? "linear-gradient(90deg, #494DA2 0%, rgba(73, 77, 162, 0.10) 100%)"
                  : "linear-gradient(90deg, #2978B5 0%, rgba(41, 120, 181, 0.10) 100%)",
              }}
            />
          </div>
          <div className="container qpContentWrapper">
            <div className="row">
              <div className="col-lg-8">
                <div className="qpPlanSection">
                  <h2 className="qpPlanTitle">{quotationDetail?.name}</h2>
                  <span className="qpCreatedText">
                    Posted {getTimeAgo(quotationDetail?.createdAt)}
                  </span>

                  <div className="outerSectionCampaign qpDescription">
                    <h6 className="m-0 titleCampagin">Description</h6>
                    <p
                      className="qpPlanDescription m-0"
                      dangerouslySetInnerHTML={{
                        __html: quotationDetail?.targetAudience,
                      }}
                    ></p>
                  </div>
                </div>

                {/* Organizer */}
                <div className="qphrLine" />
                <h5 className="qpOrganizerTitle m-0">Organizer</h5>
                <div className="qpOrganizerBox qpOrganizerBoxDetailed ">
                  <div className="qpOrganizerRow">
                    <div className="qpOrganizerProfile">
                      <img
                        className="qpOrganizerAvatar"
                        src={imageUrl || placeholder}
                        alt="profile"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = placeholder;
                        }}
                      />

                      <div>
                        <div className="qpOrganizerName">{organizerName} {company?.isAdvisor && (
                          <img
                            src="/assets/img/isAdvisorBadge.svg"
                            alt="Advisor verified"
                          />
                        )}</div>
                      </div>
                    </div>

                    <p
                      onClick={() =>
                        navigate(RoutesPaths.SHARE_PAGE.replace(":id", company?._id,))
                      }
                      className="qpViewProfileLink"
                    >
                      View profile
                    </p>
                  </div>

                  <div className="qpOrganizerStats">

                    {(advisorNumbers) && (
                      <div className="numberHolder advisorNumbersHolder">
                        {(advisorNumbers).map((metric) => (
                          <div
                            className="numberHolder advisorNumberItem"
                            aria-label={`${metric.label}: ${metric.displayValue}`}
                            key={metric.key}
                          >
                            <img src={metric.icon} alt="" />
                            <div className="advisorNumberContent">
                              <p>{metric.displayValue}</p>
                              <span>{metric.label}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {(organizerLocation || organizerJoinedDate) && (
                    <div className="qpOrganizerMeta">
                      {organizerLocation && (
                        <div className="qpOrganizerMetaItem">
                          <img src="/assets/img/Location-icon.svg" alt="" />
                          <p>{organizerLocation}</p>
                        </div>
                      )}
                      {organizerLocation && organizerJoinedDate && (
                        <span className="inlineDotSeparator" />
                      )}
                      {organizerJoinedDate && (
                        <p>Joined on {formatJoinedDate(organizerJoinedDate)}</p>
                      )}
                    </div>
                  )}

                  {organizerAbout && (
                    <div className="qpOrganizerAbout">
                      <h6>About {isAdvisorUser ? "advisor" : "organizer"}</h6>
                      <p>{organizerAbout}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Quotation Card */}
              <div className="col-lg-4">
                <div className="caAnalyticsBar">
                  <div className="caAnalyticsBarHeader">
                    <h3 className="m-0">Quotation</h3>
                  </div>

                  <div className="advisoraboutOuter">
                    <p className="qpQuotationText">
                      This quotation has been prepared for you by{" "}
                      <b>
                        {quotationDetail?.createdBy?.first_name +
                          " " +
                          quotationDetail?.createdBy?.last_name}
                      </b>
                      . Please review the details and proceed with payment to
                      confirm.
                    </p>
                    {quotationDetail?.attachment?.length > 0 && (
                      <div className="infoRowNew p-0">
                        <p className="amoutLabel pt-2 pb-2">Attachment</p>
                        <div className="infoRow infoRowAttachment m-0 border-0">
                          <AttachmentList
                            isAdvisor={isAdvisorUser}
                            planPreview={quotationDetail}
                          />
                        </div>
                      </div>
                    )}
                    <p className="amoutLabel pt-3">Quoted amount</p>
                    <div className="amoutHolderInput">
                      <p className="amountText">{quotationDetail?.price}</p>
                      <div className="currenctTextHolder">
                        <img
                          src={`assets/img/${quotationDetail?.currency?.value}.png`}
                          alt={quotationDetail?.currency?.value}
                          style={{ width: 20, height: 20 }}
                        />
                        <p className="currenctText">
                          <Currency
                            currency={quotationDetail?.currency?.value || ""}
                          />
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          RoutesPaths.PAY_BANKTRANSFER_PUBLIC.replace(
                            ":slug",
                            planId,
                          ),
                        )
                      }
                      className="qpContinueBtn"
                    >
                      <p>Continue</p>
                    </button>
                  </div>
                </div>

                <OtherQuotationAdvisor
                  isAdvisorUser={isAdvisorUser}
                  payouts={quotationDetail?.payouts}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <PublicPageFooter />
    </div>
  );
}
